import { useEffect, useRef, useState } from 'react';
import { OptionList } from './Reference.js';

/**
 * The rules summary, shown from the question mark in the masthead.
 *
 * The text is the project owner's, transcribed as given. It is a
 * reference for players mid-game, not documentation, so it stays terse
 * and is not rephrased.
 */

interface HelpProps {
  onClose: () => void;
  /**
   * Whether debris is in play, when the reader is in a game.
   *
   * `null` outside a game -- the lobby, the course editor -- where there
   * is no game to answer for. The debris note is shown then, since it is
   * a base rule and the summary is being read in general rather than
   * against a particular game's settings.
   */
  debris?: boolean | null;
  /**
   * Whether the game is in turn mode, which nobody is asked anything in
   * once programming closes: the chop shop and the double wrench settle
   * themselves. `null` outside
   * a game, read as normal mode.
   */
  turnMode?: boolean | null;
}

export function Help({ onClose, debris = null, turnMode = null }: HelpProps) {
  const panel = useRef<HTMLDivElement>(null);
  const [optionsOpen, setOptionsOpen] = useState(false);

  // Escape closes it, and focus moves into the panel so a keyboard user
  // is not left behind on the button that opened it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
    <div
      className="help-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="help-panel plate"
        role="dialog"
        aria-modal="true"
        aria-label="Rules summary"
        tabIndex={-1}
        ref={panel}
        // A click inside must not reach the backdrop, which closes.
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hazard hazard-rule" role="presentation" />

        <div className="help-body">
          <div className="spread">
            <h3>Rules summary</h3>
            <button className="secondary" onClick={onClose}>Close</button>
          </div>

          <ul className="help-notes">
            <li>Robots are virtual on Turn 1, and by register afterwards.</li>
            <li>Millennium Falcon applies to timed pits and flame throwers.</li>
            <li>Must be on last flag on last card on last turn to win.</li>
            <li>Robots on a Pit Stop deactivate the next phase, except register 5.</li>
            {/* Hidden when the host switched debris off, so a player is
                never told a rule this game does not use. */}
            {debris !== false && (
              <li>
                Debris appears when a robot is destroyed or receives 2+ damage
                during a register phase. A robot that enters and exits debris
                during a single register phase receives 1 damage.
              </li>
            )}
            <li>
              <a href="#" onClick={(e) => { e.preventDefault(); setOptionsOpen(true); }}>
                All Option cards
              </a>
            </li>
          </ul>

          <p className="eyebrow">Turn sequence</p>

          <ol className="help-sequence">
            <li>
              Programming:
              <ul>
                <li>Robots set direction (Turn 1 and returning robots)</li>
                <li>Returning robots holding more than one option give one up</li>
                <li>Powered-down and returning robots decide whether to stay powered down</li>
                <li>Arrange program cards</li>
                {turnMode && <li>Switch on Options for the turn</li>}
                <li>Decide power down</li>
              </ul>
            </li>

            <li>
              Board Elements:
              <ul>
                <li>Robots Move (Flamers damage)</li>
                <li>Express convey 1</li>
                <li>Express/Single convey 1</li>
                <li>Current convey 1</li>
                <li>Pushers push (by register phase)</li>
                <li>Gears rotate</li>
              </ul>
            </li>

            <li>
              Weapons Fire:
              <ul>
                <li>Weapons fire; Flamers/Crushers activate (on their registers)</li>
              </ul>
            </li>

            <li>
              End of Phase:
              <ul>
                <li>Touch checkpoints</li>
                <li>
                  {turnMode
                    ? 'Chop shop activates (reload the emptiest; new option on register 5)'
                    : 'Chop shop activates (reload/exchange)'}
                </li>
                <li>Robots devirtualize (except Turn 1)</li>
                <li>Pit Stop heals (except register 5)</li>
              </ul>
            </li>

            <li>
              End of Turn:
              <ul>
                <li>
                  {turnMode
                    ? 'Repair (double wrench: heal 1 and an option)'
                    : 'Repair'}
                </li>
                <li>New options (as appropriate)</li>
                <li>Robots devirtualize (Turn 1)</li>
                <li>Win game</li>
              </ul>
            </li>
          </ol>

          {/* Outside a game too, where the mode is not known. */}
          {turnMode !== false && (
            <>
              <p className="eyebrow">Turn mode</p>

              <ul className="help-notes">
                <li>
                  Every decision is made while programming. Once the last program
                  is in, the whole turn runs straight through, all five registers
                  and end of turn, without interruption. The turn is then played
                  back. Programming the next turn opens when the playback ends or
                  you stop it.
                </li>
                <li>
                  Options that pause a register are replaced by turn
                  versions that are set while programming. A move option, such as
                  Brakes or Fourth Gear, is programmed by register. The Big Gun
                  is programmed the same way, for each register it should fire
                  in. Only one optional weapon may be selected each turn. A selected
                  weapon fires instead of the main laser in every register where
                  it would do something. Options that inherently interrupt the
                  turn are not included (e.g., Abort Switch).
                </li>
                <li>
                  Other choices have default answers: the chop shop gives a new
                  option on register 5 and otherwise reloads the emptiest option;
                  the double wrench heals 1 and gives an option, as a
                  wrench/hammer does.
                </li>
                <li>
                  You are emailed when the game is waiting on you, unless you
                  turn email notification off in your account panel.
                </li>
              </ul>
            </>
          )}

          <p className="eyebrow">Bots</p>

          <ul className="help-notes">
            <li>
              Run from your own computer, bots play in turn-mode games you
              host that permit bots:<a href="/downloads/rallyx-bot.exe" download>download it</a> (Windows,
              93 MB). Bot functionality is documented in <a href="/downloads/README.md" target="_blank" rel="noreferrer">their README</a>.
            </li>
            <li>
              To list the bots and how each plays:
              <span className="mono" style={{ display: 'block', margin: '0.2rem 0' }}>rallyx-bot</span>
            </li>
            <li>
              To seat Hammer Bot in game 44, with the bot token from your
              account panel (token not needed if you saved bot.token in the same
              folder as the exe):
              <span className="mono" style={{ display: 'block', margin: '0.2rem 0' }}>rallyx-bot hammerbot 44 rxb_...</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
    {optionsOpen && (
      <OptionList
        onClose={() => setOptionsOpen(false)}
        turnMode={turnMode === true}
        debris={debris !== false}
      />
    )}
    </>
  );
}
